import PageHead from '@/components/PageHead';
import PageLayout from '@/components/layout/page-layout';
import Container from '@/components/Container';
import Timeline from '@/components/TimelineHistoriaInstituto';
import HeadingOne from '@/components/Heading/HeadingOne';
import HorizontalRow from '@/components/HorizontalRow';
import { useAccessibilityStore } from '@/contexts/accessibility';

import NextImage from 'next/image';
import NextLink from 'next/link';

import {
	MdAdsClick,
	MdOutlineRemoveRedEye,
	MdOutlineVolunteerActivism,
} from 'react-icons/md';

import {
	FiHeart
} from 'react-icons/fi'

import { IoMdHeartEmpty } from 'react-icons/io';
import { ExtendProps } from 'app';
import { HTMLAttributes } from 'react';

type OsdFigure = HTMLAttributes<HTMLElement> & {
	image: {
		src: string;
		label: string;
		caption: string;
	};
};
function OdsFigure(props: ExtendProps<OsdFigure>) {
	const { className } = props;

	return (
		<figure
			className={`w-[100%] flex items-center gap-4 lg:flex-col relative ${
				className ? className : ''
			}`}
		>
			<picture tabIndex={0}>
				<source srcSet={props.image.src} media="(min-width: 0)" />
				<NextImage
					className="min-w-[144px] h-auto"
					src={props.image.src}
					alt={props.image.label}
					aria-label={props.image.label}
					width={144}
					height={144}
				/>
			</picture>
			<figcaption className="font-bold lg:text-center">
				{props.image.caption}
			</figcaption>
		</figure>
	);
}

export default function Page() {
	const accessibilityStore = useAccessibilityStore();
	const pallet = accessibilityStore.getPallet();

	return (
		<>
			<PageHead
				page={{
					title: 'Instituto Deixe Vivo | Nossa História',
					keywords: 'OSC, Instituto, Deixe Vivo, Doação de órgãos e tecidos, transplante, transplantados, transplantes, renal, tecidos, história, trajetória, linha do tempo, missão, visão, valores, propósito, objetivos de desenvolvimento sustentável',
					colorScheme: 'light dark',
					description: 'Nascemos com o propósito de deixar viva a cultura da doação de órgãos e tecidos em toda a sociedade',
					robots: 'follow, index',
				}}
				og={{
					description: 'Nascemos com o propósito de deixar viva a cultura da doação de órgãos e tecidos em toda a sociedade',
					site_name: 'Instituto Deixe Vivo | Nossa História',
					title: 'Instituto Deixe Vivo | Nossa História',
					type: 'website',
					url: 'https://deixevivo.org.br',
					image: {
						alt: 'Logo do Instituto, ao lado esquerdo da imagem um rosto feliz e ao lado direito texto que diz Instituto Deixe Vivo',
						fbAdmins: 'instituto.deixevivo',
						src: 'https://res.cloudinary.com/oliveiradev/image/upload/v1681919729/InstitutoDeixeVivo/logos/logo-deixe-vivo-1200x1200.jpg',
						type: 'image/jpg',
						width: '1200',
						height: '1200',
					},
				}}
				twitter={{
					card: 'summary',
					description: 'Nascemos com o propósito de deixar viva a cultura da doação de órgãos e tecidos em toda a sociedade',
					site: 'Instituto Deixe Vivo | Nossa História',
					title: 'Instituto Deixe Vivo | Nossa História',
					url: 'https://deixevivo.org.br',
					image: {
						alt: 'Logo do Instituto, ao lado esquerdo da imagem um rosto feliz e ao lado direito texto que diz Instituto Deixe Vivo',
						src: 'https://res.cloudinary.com/oliveiradev/image/upload/v1681919729/InstitutoDeixeVivo/logos/logo-deixe-vivo-1200x1200.jpg',
					},
				}}
			/>
			<PageLayout>
				<Container className="py-8">
					<HeadingOne horizontalRow>Nossa História</HeadingOne>

					<div className="mt-8">
						<section>
							<Timeline />

							<p className="font-bold" tabIndex={0}>
								Você também pode fazer parte da construção dessa história!
								Contribua para que o Instituto continue crescendo e
								transformando o cenário da doação de órgãos e tecidos no Brasil,
								além da realidade de todos os envolvidos: pacientes renais
								crônicos, pessoas que esperam por um transplante e
								transplantados.
							</p>

							<div className="mt-4">
								<NextLink
									aria-label="Link para página de apoio ao Instituto Deixe Vivo"
									tabIndex={0}
									className={`
								button
								bg-primary-dark
								text-white
								rounded
								shadow-md
								uppercase
								w-max
								font-bold
						`}
									href="/apoio"
								>
									<strong>Quero contribuir com a causa!</strong>
								</NextLink>
							</div>
						</section>
					</div>

					<div className="my-12">
						<HorizontalRow />
					</div>

					{/* p-8 bg-white rounded-lg shadow-lg flex justify-between h-full lg:h-[340px] flex-wrap */}
					<div className="py-8 shadow-md rounded-lg">
						<section tabIndex={0} className="flex flex-col gap-y-4 lg:flex-row">
							<div className="flex flex-col items-center gap-y-2 p-4 text-center w-[100%]">
								<MdOutlineVolunteerActivism className="text-primary-dark text-3xl" />
								<h2 className="font-bold text-xl" tabIndex={0}>
									Propósito
								</h2>
								<p tabIndex={0}>
									Deixar viva a cultura da doação de órgãos e tecidos.
								</p>
							</div>

							<div className="flex flex-col items-center gap-y-2 p-4 text-center w-[100%]">
								<MdAdsClick className="text-primary-dark text-3xl" />
								<h2 className="font-bold text-xl" tabIndex={0}>
									Missão
								</h2>
								<p tabIndex={0}>
									Despertar na sociedade a cultura da doação de órgãos e
									tecidos, acolher pacientes em lista de espera, transplantados
									e seus familiares, todos por meio da informação.
								</p>
							</div>

							<div className="flex flex-col items-center gap-y-2 p-4 text-center w-[100%]">
								<MdOutlineRemoveRedEye className="text-primary-dark text-3xl" />
								<h2 className="font-bold text-xl" tabIndex={0}>
									Visão
								</h2>
								<p tabIndex={0}>
									Em cinco anos ser o maior movimento de conscientização e
									inspiração sobre doação de órgãos e tecidos.
								</p>
							</div>

							<div className="flex flex-col items-center gap-y-2 p-4 text-center w-[100%]">
								<FiHeart className="text-primary-dark text-3xl" />
								<h2 className="font-bold text-xl" tabIndex={0}>
									Valores
								</h2>
								<p tabIndex={0}>
									Resiliência, confiabilidade, otimismo, empatia, criatividade,
									flexibilidade, fortalecimento do capital humano.
								</p>
							</div>
						</section>
					</div>

					<div className="my-12">
						<HorizontalRow />
					</div>

					<div className="mb-16">
						<section>
							<h2 className="font-bold text-xl" tabIndex={0}>
								Objetivos de Desenvolvimento Sustentável
							</h2>

							<div className="flex flex-col lg:flex-row gap-y-4	justify-between relative mt-8">
								<OdsFigure
									className="lg:top-[40px]"
									image={{
										src: '/assets/images/desenvolvimento-sustentavel/1.png',
										caption: 'Saúde e bem-estar',
										label: 'Saúde e bem-estar',
									}}
								/>
								<OdsFigure
									className="lg:top-[20px]"
									image={{
										src: '/assets/images/desenvolvimento-sustentavel/2.png',
										caption: 'Trabalho decente e crescimento econômico',
										label: 'Trabalho decente e crescimento econômico',
									}}
								/>
								<OdsFigure
									image={{
										src: '/assets/images/desenvolvimento-sustentavel/3.png',
										caption: 'Resolução das desigualdades',
										label: 'Resolução das desigualdades',
									}}
								/>
								<OdsFigure
									className="lg:top-[20px]"
									image={{
										src: '/assets/images/desenvolvimento-sustentavel/4.png',
										caption: 'Consumo e produção responsáveis',
										label: 'Consumo e produção responsáveis',
									}}
								/>
								<OdsFigure
									className="lg:top-[40px]"
									image={{
										src: '/assets/images/desenvolvimento-sustentavel/5.png',
										caption: 'Parcerias e meios de implementação',
										label: 'Parcerias e meios de implementação',
									}}
								/>
							</div>
						</section>
					</div>
				</Container>
			</PageLayout>
		</>
	);
}
